import {
  Col,
  Row,
  Space,
  Table,
  Button,
  Tag,
  message,
  Popconfirm,
  Descriptions,
  Typography,
  Tooltip,
  Input,
} from "antd"
import {
  HistoryOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CarOutlined,
  UndoOutlined,
  CopyOutlined,
  SearchOutlined,
} from "@ant-design/icons"
import { useEffect, useState, useMemo, useRef } from "react"
import "./Rolls.css"
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  where,
  doc,
  runTransaction,
} from "firebase/firestore"
import moment from "moment"
import PageHeader from "../../../components/Elements/PageHeader"
import { db } from "../../../firebase/config"
import SoldRollsExport from "../../../components/Rolls/SoldRollsExport"

const { Text } = Typography

// ---------- utils ----------
const isSold = (r) => r && r.shipping === true && r.shippedAt && r.shippedAt.seconds
const ts = (t) => (t?.seconds ? moment.unix(t.seconds).format("DD/MM/YYYY HH:mm") : "-")
const dayKey = (t) => (t?.seconds ? moment.unix(t.seconds).startOf("day").valueOf() : null)
const dayLabel = (ms) => (ms ? moment(ms).format("DD.MM.YYYY") : "-")

export default function RollHistory() {
  const [historyRollsLoading, setHistoryRollsLoading] = useState(true)
  const [historyRollsData, setHistoryRollsData] = useState([])

  // Shared date filter (used by export toolbar). Keeps your previous UX:
  const [pickerRange, setPickerRange] = useState(null) // [dayjs, dayjs] | null
  const [rangeMs, setRangeMs] = useState([null, null]) // [startMs, endMs]

  const materialEanSearchRef = useRef(null)
  const intLV = new Intl.NumberFormat("lv-LV", { maximumFractionDigits: 0 })

  const materialEanSearchProps = {
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={materialEanSearchRef}
          placeholder="Search material or EAN"
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}>
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters?.()
              confirm()
            }}
            size="small"
            style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, group) => {
      const haystack = `${group.materialName ?? ""} ${group.ean ?? ""}`.toLowerCase()
      return haystack.includes(String(value).toLowerCase())
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setTimeout(() => materialEanSearchRef.current?.select(), 100)
    },
  }

  // Load history
  useEffect(() => {
    setHistoryRollsLoading(true)
    const historyRef = collection(db, "rollStoreHistory")
    const qRef = query(historyRef, orderBy("created", "desc"))
    const unsubscribe = onSnapshot(
      qRef,
      (snapshot) => {
        const results = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }))
        setHistoryRollsData(results)
        setHistoryRollsLoading(false)
      },
      (error) => {
        console.error("Error fetching roll history: ", error)
        message.error("Failed to load roll history.")
        setHistoryRollsLoading(false)
      }
    )
    return () => unsubscribe()
  }, [])

  // Apply SOLD + shippedAt date range filter (unchanged from your current logic)
  const filteredSoldRolls = useMemo(() => {
    let rows = historyRollsData.filter(isSold)
    const [startMs, endMs] = rangeMs
    if (startMs && endMs) {
      rows = rows.filter((r) => {
        const t = r?.shippedAt?.seconds ? r.shippedAt.seconds * 1000 : null
        return t && t >= startMs && t <= endMs
      })
    }
    return rows
  }, [historyRollsData, rangeMs])

  const euroFmt = useMemo(
    () => new Intl.NumberFormat("lv-LV", { style: "currency", currency: "EUR" }),
    []
  )

  const filteredTotals = useMemo(() => {
    const rolls = filteredSoldRolls
    const totalRolls = rolls.length
    const totalMeters = rolls.reduce((s, r) => s + Number(r?.remainingMeters ?? 0), 0)
    const totalPrice = rolls.reduce((s, r) => s + Number(r?.price ?? 0), 0)
    return { totalRolls, totalMeters, totalPrice }
  }, [filteredSoldRolls])

  // ---- Grouping: by material.EAN + rollWidth + receivedInStock (day) ----
  const groupedRows = useMemo(() => {
    const map = new Map()
    for (const r of filteredSoldRolls) {
      const ean = r?.material?.EAN ?? null
      const width = r?.rollWidth ?? null
      const rcvDayMs = dayKey(r?.receivedInStock)

      // group only when both EAN and width exist; otherwise, bucket separately
      const gKey = `${ean ?? "NOEAN"}|${width ?? "NOWIDTH"}|${rcvDayMs ?? "NODATE"}`
      if (!map.has(gKey)) {
        map.set(gKey, {
          key: gKey,
          ean,
          width,
          dateMs: rcvDayMs,
          materialName: r?.material?.material ?? "(No material name)",
          rolls: [],
          count: 0,
          totalPrice: 0,
          totalMeters: 0,
        })
      }
      const g = map.get(gKey)
      g.rolls.push(r)
      g.count += 1
      g.totalPrice += Number(r?.price ?? 0)
      g.totalMeters += Number(r?.remainingMeters ?? 0)
    }

    // stable sort: Material name, then EAN, then Width, then Date desc
    const arr = Array.from(map.values())
    arr.sort((a, b) => {
      const byMat = (a.materialName || "").localeCompare(b.materialName || "")
      if (byMat !== 0) return byMat
      const byEan = String(a.ean ?? "").localeCompare(String(b.ean ?? ""))
      if (byEan !== 0) return byEan
      const byWidth = (a.width ?? 0) - (b.width ?? 0)
      if (byWidth !== 0) return byWidth
      return (b.dateMs ?? 0) - (a.dateMs ?? 0)
    })
    return arr
  }, [filteredSoldRolls])

  // Restore Roll (unchanged)
  const handleRestoreRoll = async (rollToRestore) => {
    if (!rollToRestore.barcode) {
      message.error("This roll has no barcode and cannot be restored.")
      return
    }
    try {
      await runTransaction(db, async (transaction) => {
        const rollsRef = collection(db, "rolls")
        const barcodeQuery = query(rollsRef, where("barcode", "==", rollToRestore.barcode))
        const querySnapshot = await transaction.get(barcodeQuery)
        if (querySnapshot.empty) {
          throw new Error(
            `Barcode ${rollToRestore.barcode} not found in the 'rolls' collection. Restore aborted.`
          )
        }
        const rollDocToDeleteRef = querySnapshot.docs[0].ref
        transaction.delete(rollDocToDeleteRef)

        const historyDocRef = doc(db, "rollStoreHistory", rollToRestore.id)
        const newStoreDocRef = doc(db, "rollStore", rollToRestore.id)
        const { id, ...dataToRestore } = rollToRestore
        transaction.set(newStoreDocRef, dataToRestore)
        transaction.delete(historyDocRef)
      })
      message.success(`Roll ${rollToRestore.barcode} was successfully restored.`)
    } catch (error) {
      console.error("Failed to restore roll:", error)
      message.error(`Error: ${error.message}`)
    }
  }

  // ---------- Group table columns ----------
  const groupColumns = [
    {
      title: "Material (EAN)",
      key: "materialEan",
      ellipsis: true,
      align: "left",
      ...materialEanSearchProps,
      width: "25%",
      render: (_, group) => {
        return (
          <Space>
            {group.ean ? (
              <Text copyable={{ text: String(group.ean) }} style={{ cursor: "pointer" }}>
                <Text>{group.materialName}</Text>
                <Text type="secondary" style={{ color: "#1677ff", marginLeft: 6 }}>
                  {group.ean}
                </Text>
              </Text>
            ) : (
              <Text>{group.materialName} | —</Text>
            )}
          </Space>
        )
      },
      sorter: (a, b) =>
        (a.materialName || "").localeCompare(b.materialName || "") ||
        String(a.ean ?? "").localeCompare(String(b.ean ?? "")),
    },

    {
      title: "Width (mm)",
      dataIndex: "width",
      align: "left",
      width: "15%",
      sorter: (a, b) => (a.width ?? 0) - (b.width ?? 0),
    },
    {
      title: "Rolls",
      dataIndex: "count",
      align: "left",
      width: "15%",
      sorter: (a, b) => a.count - b.count,
    },
    {
      title: "Meters (sum)",
      dataIndex: "totalMeters",
      align: "left",
      width: "20%",
      render: (v) => <Text strong>{intLV.format(Number(v || 0))}</Text>,
      sorter: (a, b) => Number(a.totalMeters || 0) - Number(b.totalMeters || 0),
    },
    {
      title: "Date received",
      dataIndex: "dateMs",
      align: "left",
      width: "20%",
      render: (ms) => dayLabel(ms),
      sorter: (a, b) => (a.dateMs ?? 0) - (b.dateMs ?? 0),
      defaultSortOrder: "descend",
    },
    {
      title: "Total Price (€)",
      dataIndex: "totalPrice",
      align: "left",
      width: 140,
      render: (v) => {
        const n = Number(v ?? 0)
        const euro = new Intl.NumberFormat("lv-LV", { style: "currency", currency: "EUR" })
        const type = n > 0 ? "success" : n < 0 ? "danger" : "secondary"
        return <Text type={type}>{euro.format(n)}</Text>
      },
      sorter: (a, b) => Number(a.totalPrice || 0) - Number(b.totalPrice || 0),
    },
  ]

  // ---------- Inner (group → rolls) table ----------
  const rollColumns = [
    {
      title: "Status",
      key: "rollStatus",
      align: "center",
      width: 180,
      render: (_, record) => {
        if (record.shipping) {
          return (
            <Tag icon={<CarOutlined />} color="blue">
              Shipped
            </Tag>
          )
        }
        if (record.inOrder) {
          return (
            <Tag icon={<ExclamationCircleOutlined />} color="purple">
              Was In Order ({record.fullOrderNumber || `ID ...${record.orderId?.slice(-4)}`})
            </Tag>
          )
        }
        if (!record.verified && !record.inOrder && !record.shipping) {
          return (
            <Tag icon={<ClockCircleOutlined />} color="default">
              Archived (Unverified)
            </Tag>
          )
        }
        return <Tag color="gray">Archived (In Stock)</Tag>
      },
    },
    {
      title: "Barcode",
      dataIndex: "barcode",
      width: 180,
    },
    {
      title: "Meters",
      dataIndex: "remainingMeters",
      align: "center",
      width: 110,
      sorter: (a, b) => (a.remainingMeters ?? 0) - (b.remainingMeters ?? 0),
    },
    {
      title: "Price (€)",
      dataIndex: "price",
      align: "right",
      width: 110,
      render: (v) => (v || v === 0 ? Number(v).toFixed(2) : "-"),
    },
    {
      title: "Order",
      dataIndex: "fullOrderNumber",
      width: 120,
      render: (v, r) => v || r.orderId || "-",
    },
    {
      title: "Shipped At",
      dataIndex: "shippedAt",
      width: 150,
      render: (t) => ts(t),
    },
    {
      title: "Received",
      dataIndex: "receivedInStock",
      width: 140,
      render: (t) => (t?.seconds ? moment.unix(t.seconds).format("DD.MM.YYYY") : "-"),
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      align: "center",
      width: 120,
      render: (_, record) => (
        <Popconfirm
          title="Restore this roll?"
          description="This moves it to the main store and updates records. Are you sure?"
          onConfirm={() => handleRestoreRoll(record)}
          okText="Yes, Restore"
          cancelText="Cancel">
          <Button type="primary" ghost icon={<UndoOutlined />} size="small">
            Restore
          </Button>
        </Popconfirm>
      ),
    },
  ]

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <PageHeader icon={<HistoryOutlined />} text="Admin History (Grouped)" />
        </Col>

        {/* Export toolbar (unchanged; controls shippedAt date-range filter) */}
        <Col span={24}>
          <SoldRollsExport
            data={historyRollsData} // pass raw data; component re-filters too
            loading={historyRollsLoading}
            pickerRange={pickerRange}
            rangeMs={rangeMs}
            onChangeRange={(v) => {
              setPickerRange(v || null)
              if (v && v[0] && v[1]) {
                const start = v[0].startOf("day").valueOf()
                const end = v[1].endOf("day").valueOf()
                setRangeMs([start, end])
              } else {
                setRangeMs([null, null])
              }
            }}
          />
        </Col>
        <Col span={24}>
          <Space wrap size="middle">
            <Tag color="geekblue">
              Filtered Rolls: <Text style={{ marginLeft: 6 }}>{filteredTotals.totalRolls}</Text>
            </Tag>
            <Tag color="green">
              Filtered Meters (sum):{" "}
              <Text style={{ marginLeft: 6 }}>{filteredTotals.totalMeters} m</Text>
            </Tag>
            <Tag color="gold">
              Filtered Total (€):{" "}
              <Text style={{ marginLeft: 6 }}>{euroFmt.format(filteredTotals.totalPrice)}</Text>
            </Tag>
          </Space>
        </Col>

        {/* GROUPED table */}
        <Col span={24}>
          <Table
            className="groupedTable"
            dataSource={groupedRows}
            loading={historyRollsLoading}
            rowKey={(group) => group.key}
            columns={groupColumns}
            size="small"
            pagination={false}
            scroll={{ x: 1100, y: "calc(100vh - 280px)" }}
            expandable={{
              // 1st expansion: show rolls in the group as a nested table
              expandedRowRender: (group) => (
                <Table
                  dataSource={group.rolls}
                  rowKey={(r) => r.id}
                  columns={rollColumns}
                  size="small"
                  pagination={false}
                  scroll={{ x: 1200 }}
                  expandable={{
                    // 2nd expansion: per roll details
                    expandedRowRender: (record) => <RollDetails record={record} />,
                  }}
                />
              ),
            }}
          />
        </Col>
      </Row>
    </div>
  )
}

// ---------- Expanded row content (unchanged) ----------
function RollDetails({ record }) {
  return (
    <Descriptions
      size="small"
      column={3}
      bordered
      labelStyle={{ width: 180 }}
      contentStyle={{ maxWidth: 400, whiteSpace: "pre-wrap" }}>
      <Descriptions.Item label="Barcode">{record.barcode || "-"}</Descriptions.Item>
      <Descriptions.Item label="Order">
        {record.fullOrderNumber || record.orderId || "-"}
      </Descriptions.Item>
      <Descriptions.Item label="Status">
        {record.shipping
          ? "Shipped"
          : record.inOrder
          ? "In Order"
          : record.verified
          ? "In Stock"
          : "Archived (Unverified)"}
      </Descriptions.Item>

      <Descriptions.Item label="Material">{record.material?.material || "-"}</Descriptions.Item>
      <Descriptions.Item label="Supplier">
        {record.material?.supplier?.supplierName || "-"}
      </Descriptions.Item>
      <Descriptions.Item label="Supplier Material">
        {record.material?.supplierMaterialName || "-"}
      </Descriptions.Item>

      <Descriptions.Item label="Width (mm)">{record.rollWidth ?? "-"}</Descriptions.Item>
      <Descriptions.Item label="Original Meters">{record.originalMeters ?? "-"}</Descriptions.Item>
      <Descriptions.Item label="Remaining Meters">
        {record.remainingMeters ?? "-"}
      </Descriptions.Item>

      <Descriptions.Item label="Price (€)">{record.price ?? "-"}</Descriptions.Item>
      <Descriptions.Item label="Weight (kg)">{record.weight ?? "-"}</Descriptions.Item>
      <Descriptions.Item label="Core">{record.core || "-"}</Descriptions.Item>

      <Descriptions.Item label="Machine">{record.machine || "-"}</Descriptions.Item>
      <Descriptions.Item label="Dest. Machine">
        {record.destinationMachine || "-"}
      </Descriptions.Item>
      <Descriptions.Item label="Pallet / Location">
        {(record.pallet || "-") + " / " + (record.palletLocation || "-")}
      </Descriptions.Item>

      <Descriptions.Item label="Created">{ts(record.created)}</Descriptions.Item>
      <Descriptions.Item label="Updated">{ts(record.updated)}</Descriptions.Item>
      <Descriptions.Item label="Shipped At">{ts(record.shippedAt)}</Descriptions.Item>

      <Descriptions.Item label="Comment" span={3}>
        {record.comment || "-"}
      </Descriptions.Item>
    </Descriptions>
  )
}
